import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAssessment } from "../context/AssessmentContext";
import { RadarChart } from "../components/charts/RadarChart";
import { CATEGORY_COLORS } from "../lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Download,
  Check,
  AlertCircle,
  ChevronRight,
  FileText,
} from "lucide-react";

export default function Review() {
  const navigate = useNavigate();
  const {
    user,
    member,
    session,
    categories,
    items,
    responses,
    progress,
    fetchResponses,
    fetchProgress,
    submitAssessment,
    loading,
  } = useAssessment();

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!member || !session) {
      navigate("/");
      return;
    }
    
    fetchResponses();
    fetchProgress();
    
    if (member.submitted_at) {
      setIsSubmitted(true);
    }
  }, [member, session, navigate, fetchResponses, fetchProgress]);

  // Prepare radar chart data
  const radarData = categories.map((cat) => {
    const categoryItems = items[cat.id] || [];
    const categoryResponses = categoryItems
      .map((item) => responses[item.id])
      .filter((r) => r?.rating !== null && r?.rating !== undefined);
    
    const average =
      categoryResponses.length > 0
        ? categoryResponses.reduce((sum, r) => sum + r.rating, 0) / categoryResponses.length
        : 0;
    
    return {
      category_id: cat.id,
      category_name: cat.name,
      average: Math.round(average * 10) / 10,
    };
  });

  const handleSubmit = async () => {
    try {
      await submitAssessment();
      setIsSubmitted(true);
      toast.success("Assessment submitted successfully!");
      setShowSubmitDialog(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleExportPDF = () => {
    // Create printable content
    const printContent = document.getElementById("review-content");
    if (printContent) {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Life Wheel Assessment - ${user?.display_name}</title>
            <style>
              body { font-family: Inter, sans-serif; padding: 2rem; max-width: 800px; margin: 0 auto; }
              h1 { font-size: 1.5rem; margin-bottom: 0.5rem; }
              h2 { font-size: 1.25rem; margin-top: 1.5rem; border-bottom: 2px solid #2C3E50; padding-bottom: 0.5rem; }
              h3 { font-size: 1rem; margin-top: 1rem; }
              p { margin: 0.5rem 0; }
              .category { margin-bottom: 2rem; page-break-inside: avoid; }
              .item { padding: 1rem; background: #f5f5f5; margin: 0.5rem 0; border-radius: 8px; }
              .rating { font-size: 1.5rem; font-weight: bold; color: #2C3E50; }
              .label { font-weight: 600; color: #666; font-size: 0.875rem; }
              .text { white-space: pre-wrap; }
              @media print { body { padding: 0; } }
            </style>
          </head>
          <body>
            <h1>Life Wheel Assessment</h1>
            <p><strong>Participant:</strong> ${user?.display_name}</p>
            <p><strong>Session:</strong> ${session?.name}</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
            ${categories
              .map((cat) => {
                const categoryItems = items[cat.id] || [];
                return `
                  <div class="category">
                    <h2>${cat.name}</h2>
                    ${categoryItems
                      .map((item) => {
                        const response = responses[item.id];
                        return `
                          <div class="item">
                            <h3>${item.name}</h3>
                            <p><span class="label">Rating:</span> <span class="rating">${
                              response?.rating ?? "N/A"
                            }</span>/10</p>
                            ${
                              response?.q1_text
                                ? `<p><span class="label">Why this rating:</span></p><p class="text">${response.q1_text}</p>`
                                : ""
                            }
                            ${
                              response?.q2_text
                                ? `<p><span class="label">Commitment:</span></p><p class="text">${response.q2_text}</p>`
                                : ""
                            }
                          </div>
                        `;
                      })
                      .join("")}
                  </div>
                `;
              })
              .join("")}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const totalItems = Object.values(items).flat().length;
  const completedItems = Object.values(responses).filter(
    (r) => r?.rating !== null && r?.rating !== undefined
  ).length;
  const overallAverage =
    radarData.length > 0
      ? radarData.reduce((sum, d) => sum + d.average, 0) / radarData.length
      : 0;

  return (
    <div className="min-h-screen bg-background pb-8" data-testid="review-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/assessment")}
              disabled={isSubmitted}
              data-testid="btn-back"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assessment
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                data-testid="btn-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              
              {!isSubmitted && (
                <Button
                  onClick={() => setShowSubmitDialog(true)}
                  disabled={loading}
                  data-testid="btn-submit-final"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Submit
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6" id="review-content">
        {/* Status Banner */}
        {isSubmitted ? (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800">Assessment Submitted</p>
                <p className="text-sm text-green-600">
                  Thank you for completing the Life Wheel Assessment
                </p>
              </div>
            </CardContent>
          </Card>
        ) : completedItems < totalItems ? (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold text-amber-800">Incomplete Assessment</p>
                <p className="text-sm text-amber-600">
                  {completedItems} of {totalItems} items rated. You can still submit with partial responses.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Summary Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Assessment Summary</CardTitle>
            <CardDescription>
              {user?.display_name} • {session?.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Stats */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{completedItems}</p>
                    <p className="text-sm text-muted-foreground">Items Rated</p>
                  </div>
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-2xl font-bold">{overallAverage.toFixed(1)}</p>
                    <p className="text-sm text-muted-foreground">Average Score</p>
                  </div>
                </div>
                
                {/* Category scores */}
                <div className="space-y-2">
                  {radarData.map((data) => (
                    <div key={data.category_id} className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CATEGORY_COLORS[data.category_id] }}
                      />
                      <span className="flex-1 text-sm">{data.category_name}</span>
                      <Badge variant={data.average >= 7 ? "default" : data.average >= 4 ? "secondary" : "outline"}>
                        {data.average.toFixed(1)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Radar Chart */}
              <div className="radar-container">
                <RadarChart data={radarData} className="w-full h-full" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Responses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Detailed Responses
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="w-full">
              {categories.map((category) => {
                const categoryItems = items[category.id] || [];
                const categoryResponses = categoryItems
                  .map((item) => responses[item.id])
                  .filter((r) => r?.rating !== null && r?.rating !== undefined);
                
                return (
                  <AccordionItem key={category.id} value={category.id}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: CATEGORY_COLORS[category.id] }}
                        />
                        <span className="font-medium">{category.name}</span>
                        <Badge variant="outline" className="ml-2">
                          {categoryResponses.length}/{categoryItems.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-2">
                        {categoryItems.map((item) => {
                          const response = responses[item.id];
                          
                          return (
                            <div
                              key={item.id}
                              className="p-4 bg-muted/50 rounded-lg"
                              data-testid={`review-item-${item.id}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h4 className="font-medium">{item.name}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {item.description}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <span className="text-2xl font-bold">
                                    {response?.rating ?? "—"}
                                  </span>
                                  <span className="text-sm text-muted-foreground">/10</span>
                                </div>
                              </div>
                              
                              {response?.q1_text && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                    Why this rating:
                                  </p>
                                  <p className="text-sm bg-background p-2 rounded">
                                    {response.q1_text}
                                  </p>
                                </div>
                              )}
                              
                              {response?.q2_text && (
                                <div className="mt-3">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">
                                    12-Month Commitment:
                                  </p>
                                  <p className="text-sm bg-background p-2 rounded">
                                    {response.q2_text}
                                  </p>
                                </div>
                              )}
                              
                              {!isSubmitted && !response?.rating && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-2 p-0 h-auto"
                                  onClick={() => navigate("/assessment")}
                                >
                                  Add rating
                                  <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </main>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              You have rated {completedItems} of {totalItems} items.
              Once submitted, your responses cannot be edited.
              Your facilitator will be able to view your results.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={loading}>
              {loading ? "Submitting..." : "Submit Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
